import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { salesFiltersEmptyValues } from "@src/components/utils/hookFormUtils";
import { getEventOptions } from "@src/components/utils/reactSelectUtils";
import { eventActions, settingsActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { getListCategoriesSales } from "@src/store/sales/listCategoriesSlice";
import { getListHeadlinersSales } from "@src/store/sales/listHeadlinersSlice";
import { getListMarketplaces } from "@src/store/sales/listMarketplacesSlice";
import { getListSaleStatuses } from "@src/store/sales/listSalesStatusesSlice";
import { getListTags } from "@src/store/sales/listTagsSlice";
import { getListVenuesSales } from "@src/store/sales/listVenueSlice";
import { RootState } from "@src/store/store";
import { SalesFiltersProps, SalesHeaderFiltersValidation } from "@src/validation/SalesHeaderFiltersValidation";
import React, { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { SalesFilters } from "./SalesFilters";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { getDatePickerFormattedDate } from "@src/components/utils/dateUtils";

export const SalesHeader: React.FC = () => {
	const [params, setParams] = useSearchParams();
	const companyName = useAppSelector((state) => state.settings.companyName);
	const isAdmin = useAppSelector((state) => state.settings.isAdmin);
	const dispatch = useAppDispatch();

	// COMPANIES DROPDOWN
	const { data, loading } = useAppSelector((state) => state.pricing.companies);
	const companyOptions = data.map((x) => ({
		label: x,
		value: x,
	}));
	// CATEGORIES
	const categoryOptionsData = useAppSelector((state: RootState) => state.sales.listCategoriesSales.categories);
	const categoryOptions = categoryOptionsData.map((item) => ({
		label: item.Cat_Desc,
		value: item.Cat_Desc,
	}));
	// VENUES
	const venueOptionsData = useAppSelector((state: RootState) => state.sales.listVenues.venues);
	const venueOptions = venueOptionsData.map((item) => ({
		label: item.Venue_Name,
		value: item.Venue_Name,
	}));
	// HEADLINERS
	const headlinerOptionsData = useAppSelector((state: RootState) => state.sales.listHeadliners.headliners);
	const headlinerOptions = headlinerOptionsData.map((item) => ({
		label: item.Event_Headliner,
		value: item.Event_Headliner,
	}));
	// SALE STATUSES
	const saleStatusesOptionsData = useAppSelector((state: RootState) => state.sales.listSaleStatuses.saleStatuses);
	const saleStatusesOptions = saleStatusesOptionsData.map((item) => ({
		label: item,
		value: item,
	}));
	// MARKETPLACES
	const marketplacesOptionsData = useAppSelector((state: RootState) => state.sales.listMarketplaces.marketplaces);
	const marketplaceOptions = marketplacesOptionsData.map((item) => ({
		label: item.Exchange_Name,
		value: item.Exchange_Name,
	}));
	// TAGS
	const tagOptionsData = useAppSelector((state: RootState) => state.sales.listTags.tags);
	const uniqueTags = Array.from(new Set(tagOptionsData.map((item) => item.Tag)));
	const tagOptions = uniqueTags.map((item) => ({
		label: item,
		value: item,
	}));

	useEffect(() => {
		!categoryOptionsData.length && dispatch(getListCategoriesSales());
		!venueOptionsData.length && dispatch(getListVenuesSales());
		!headlinerOptionsData.length && dispatch(getListHeadlinersSales());
		!saleStatusesOptionsData.length && dispatch(getListSaleStatuses());
		!marketplacesOptionsData.length && dispatch(getListMarketplaces());
		!tagOptionsData.length && dispatch(getListTags());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	// this will be changed by next sprint
	const events = useAppSelector((state) => state.pricing.events.data.Events);
	const smartSearchOptions = getEventOptions(events, "Venue_Name");

	const setFormValues = useCallback(
		(params: URLSearchParams) => ({
			companyName: companyName ?? "",
			category: params.get("category") ?? "",
			saleFromDate: params.get("saleFromDate") ?? "",
			saleToDate: params.get("saleToDate") ?? "",
			headliner: params.get("headliner") ?? "",
			venue: params.get("venue") ?? "",
			eventFromDate: params.get("eventFromDate") ?? "",
			eventToDate: params.get("eventToDate") ?? "",
			smartSearch: params.get("smartSearch") ?? "",
			tags: params.get("tags") ?? "",
			exchangeName: params.get("exchangeName") ?? "",
			paymentFromDate: params.get("paymentFromDate") ?? "",
			paymentToDate: params.get("paymentToDate") ?? "",
			saleStatus: params.get("saleStatus") ?? "",
			sections: params.get("sections") ?? "",
			rows: params.get("rows") ?? "",
			minQuantity: params.get("minQuantity") ?? "",
			invoiceId: params.get("invoiceId") ?? "",
			eventId: params.get("eventId") ?? "",
			buyerPOId: params.get("buyerPOId") ?? "",
		}),
		[companyName],
	);

	const { control, handleSubmit, reset, setValue, getValues } = useForm({
		mode: "onChange",
		resolver: yupResolver(SalesHeaderFiltersValidation),
		defaultValues: setFormValues(params),
	});

	useEffect(() => {
		if (!companyName) return;
		setValue("companyName", companyName);
	}, [companyName, setValue]);

	const saleFromDateParam = params.get("saleFromDate") ?? "";

	useEffect(() => {
		if (getValues("saleFromDate") !== saleFromDateParam) setValue("saleFromDate", saleFromDateParam);
	}, [saleFromDateParam, getValues, setValue]);

	const optionsLoading: Record<string, boolean> = {
		category: useAppSelector((state: RootState) => state.sales.listCategoriesSales.loading),
		venue: useAppSelector((state: RootState) => state.sales.listVenues.loading),
		headliner: useAppSelector((state: RootState) => state.sales.listHeadliners.loading),
		saleStatus: useAppSelector((state: RootState) => state.sales.listSaleStatuses.loading),
		exchangeName: useAppSelector((state: RootState) => state.sales.listMarketplaces.loading),
		tags: useAppSelector((state: RootState) => state.sales.listTags.loading),
	};

	const salesFilterProps = {
		control,
		reset,
		setValue,
		getValues,
		categoryOptions,
		headlinerOptions,
		venueOptions,
		tagOptions,
		smartSearchOptions,
		marketplaceOptions,
		saleStatusesOptions,
		optionsLoading,
	};

	const onSubmit: SubmitHandler<SalesFiltersProps> = (data) => {
		Object.entries(data).forEach(([k, v]) => {
			v ? params.set(k, String(v)) : params.delete(k);
		});
		params.delete("eventsPage");
		dispatch(settingsActions.setCompanyName(data.companyName ?? ""));
		dispatch(eventActions.setEvent({ event: null }));
		params.delete("companyName");
		setParams(params);
	};

	const onFiltersReset = () => {
		const today = new Date();
		const sevenDaysAgo = new Date();
		sevenDaysAgo.setDate(today.getDate() - 7);

		const todayFormatted = getDatePickerFormattedDate(today);
		const sevenDaysAgoFormatted = getDatePickerFormattedDate(sevenDaysAgo);

		setParams({
			saleFromDate: sevenDaysAgoFormatted,
			saleToDate: todayFormatted,
		});

		reset({
			...salesFiltersEmptyValues,
			saleFromDate: sevenDaysAgoFormatted,
			saleToDate: todayFormatted,
		});
	};

	const savedFilterLS = JSON.parse(localStorage.portal_saved_sales_filters ?? null);
	const [savedFilters, setSavedFilters] = useState(savedFilterLS);

	const onFiltersSave = () => {
		const values = getValues();
		if (!savedFilters) {
			setSavedFilters([values]);
			localStorage.setItem("portal_saved_sales_filters", JSON.stringify([values]));
			return;
		}
		const combinedFilters = [...savedFilters, values];
		setSavedFilters(combinedFilters);
		localStorage.setItem("portal_saved_sales_filters", JSON.stringify(combinedFilters));
	};

	return (
		<form className={styles.panelForm} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.panelBody}>
				<SalesFilters
					{...salesFilterProps}
					companyOptions={companyOptions}
					companiesLoading={loading}
					isAdmin={isAdmin}
					savedFilters={savedFilters}
					onSaveFilters={onFiltersSave}
				/>
			</div>
			<div className={styles.panelFooter}>
				<Button
					type="button"
					variant={ButtonVariant.Secondary}
					title="Resets all filters and sets the sales date range to the last 7 days."
					onClick={onFiltersReset}>
					Reset filters
				</Button>
				<Button type="submit">Search</Button>
			</div>
		</form>
	);
};
